<template>
  <div class="container">
    <!--
      日 / 週 / 月 選択エリア
      期間選択エリアの表示切替のトリガーです。
    -->
    <div>
      <md-radio
        v-for="term in termTypes"
        :key="term.id"
        v-model="_termType"
        :value="term.id"
        >{{ term.label }}</md-radio
      >
    </div>
    <!-- 期間選択エリア -->
    <div>
      <transition mode="out-in">
        <!-- 週の選択 -->
        <div v-if="isWeekMode" key="w">
          <div class="term-input">
            <gl-input-date
              :min="minDate"
              :max="maxDate"
              :value.sync="_dateFrom"
            />
            <div class="divider">から</div>
            <md-field class="week-count">
              <md-input
                v-model.number="weekCount"
                type="number"
                :min="1"
                :max="maxWeek"
                required
              ></md-input>
            </md-field>
            <div class="divider">週間の推移を見る</div>
          </div>
        </div>
        <!-- 月の選択 -->
        <div v-if="isMonthMode" key="m">
          <div class="term-input">
            <gl-select-month
              :min="minDate"
              :max="maxDate"
              :value.sync="monthFrom"
            />
            <div class="divider">から</div>
            <md-field>
              <md-input
                v-model.number="monthCount"
                type="number"
                :min="1"
                :max="maxMonth"
                required
              ></md-input>
            </md-field>
            <div class="divider">月間の推移を見る</div>
          </div>
        </div>
      </transition>
    </div>
    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      {{ snackbar.message }}
    </md-snackbar>
  </div>
</template>

<script>
import moment from 'moment'
import GlSelectMonth from '@/components/common/GlSelectMonth.vue'
import GlInputDate from '@/components/common/GlInputDate.vue'
import {
  getEndDateByWeekCount,
  getEndDateByMonthCount,
  weekMove,
  monthMove,
  dateLimit,
} from '@/util/date'
import QueryFilterRestoreMixin from '@/mixins/QueryFilterRestoreMixin'
// 通常（このコンポーネントへの入力や出力）で利用する日付フォーマット
const DATE_FORMAT = 'YYYYMMDD'
// input:dateタグでValueに使われる日付フォーマット
const DATE_FORMAT_ON_INPUT_TAG = 'YYYY-MM-DD'
// 週の初期値設定
const DEFAULT_WEEK_COUNT = 13

/**
 * 期間選択コンポーネント
 *
 * 内部的には開始、終了日だけを保持し、週や月の値は現在選択されている日付
 * から算出して取得・設定します。
 *
 * なので、選択方式に週や月を選んでいてもこのコンポーネントから取得できるのは "日" です。
 */
export default {
  components: { GlSelectMonth, GlInputDate },
  mixins: [QueryFilterRestoreMixin],
  data() {
    return {
      termTypes: [{ id: 'w', label: '週' }, { id: 'm', label: '月' }],
      // Snackbarの表示制御用のデータ
      snackbar: {
        show: false,
        duration: 10000,
        message: '',
      },
    }
  },
  props: {
    // 選択している選択種別（日 / 週 / 月）
    termType: {
      type: String,
      default: 'w',
    },
    dateFrom: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
    dateTo: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
  },
  watch: {
    /**
     * 最大日付の変更時、現在の日付値が上限を超えていないか確認し、
     * 超えている場合は新しい上限に自動調整します。
     */
    maxDate() {
      this.adjustRangeIfNeeded()
    },
    minDate() {
      this.adjustRangeIfNeeded()
    },
    /**
     * 選択種別の変更時、現在設定されている開始・終了日から、変更先の種別で適切となる
     * 開始・終了日に変換します。
     *
     * 例）"日" ⇒ "月" に変更した場合、"開始日" は月初の日付になり、"終了日" は月末の日付になります。
     *     2019/01/14～2019/05/03 -> 2019/01(2019/01/01)～2019/05(2019/05/31)
     */
    termType(value) {
      this.$emit('update:termType', value)
      let dateFrom = this.dateFrom
      let dateTo = this.dateTo
      // dateFrom, dateToはpropでもらい、親コンポーネントと.syncするので、
      // `update:dateFrom`, `update:dateTo` をemitするだけです。
      switch (value) {
        case 'w':
          // 週の場合終了日は表示されませんが、内部的には開始日からx週間後の日付を保持します。
          this.$emit(
            'update:dateTo',
            getEndDateByWeekCount(dateFrom, this.weekCount),
          )
          break
        case 'm':
          // ここで、月初、月末に変えます
          dateFrom = moment(dateFrom, DATE_FORMAT_ON_INPUT_TAG)
            .startOf('month')
            .format(DATE_FORMAT)
          dateTo = moment(dateTo, DATE_FORMAT_ON_INPUT_TAG)
            .endOf('month')
            .format(DATE_FORMAT)
          dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
          dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
          this.$emit('update:dateTo', dateTo)
          this.$emit('update:dateFrom', dateFrom)
          break
        default:
          break
      }
    },
  },
  methods: {
    adjustRangeIfNeeded() {
      let dateFrom = this.dateFrom
      let dateTo = this.dateTo
      // QueryFilterRestoreMixinのcomputedを参照
      if (this._queryFilterRestoring) return

      const adjustedFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
      const adjustedTo = dateLimit(dateTo, this.minDate, this.maxDate)

      if (adjustedFrom !== dateFrom || adjustedTo !== dateTo) {
        this.$emit('update:dateFrom', adjustedFrom)
        this.$emit('update:dateTo', adjustedTo)
        this.snackbar.message =
          '指定期間が選択可能な期間外だったため、自動調整しました。'
        this.snackbar.show = true
      }
    },
    // QueryFilterRestoreMixinのmethodsを上書き
    // 分析条件の復元が完了したときの処理
    onQueryFilterRestoreCompleted() {
      this.adjustRangeIfNeeded()
    },
  },
  computed: {
    minDate() {
      return this.$store.getters.calendarEffectiveMinDate
    },
    maxDate() {
      return this.$store.getters.calendarEffectiveMaxDate
    },

    _termType: {
      get() {
        if (!this.termTypes.some(m => m.id === this.termType)) {
          this.$emit('update:termType', this.termTypes[0].id || 'w')
        }
        return this.termType
      },
      set(newVal) {
        this.$emit('update:termType', newVal)
      },
    },
    isWeekMode() {
      return this.termType === 'w'
    },
    isMonthMode() {
      return this.termType === 'm'
    },
    maxWeek() {
      return moment(this.maxDate).diff(this.dateFrom, 'weeks') + 1
    },
    maxMonth() {
      return moment(this.maxDate).diff(this.dateFrom, 'months') + 1
    },
    weekCount: {
      get() {
        return moment(this.dateTo).diff(moment(this.dateFrom), 'weeks') + 1
      },
      set(newVal) {
        let dateTo = getEndDateByWeekCount(this.dateFrom, newVal)
        this.$emit('update:dateTo', dateTo)
      },
    },
    // このコンポーネントの入出力となる開始・終了日のフォーマットと、
    // inputに設定する値の日付フォーマットが異なるので変換用computedプロパティを用意
    _dateFrom: {
      get() {
        return this.dateFrom
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        let dateTo = weekMove(this.dateTo, this.dateFrom, newVal)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', newVal)
        this.$emit('update:dateTo', dateTo)
      },
    },
    monthFrom: {
      get() {
        return moment(this.dateFrom).format('YYYYMM')
      },
      set(newVal) {
        newVal = moment(newVal, DATE_FORMAT)
          .startOf('month')
          .format(DATE_FORMAT)
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        let dateTo = monthMove(this.dateTo, this.dateFrom, newVal)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', newVal)
        this.$emit('update:dateTo', dateTo)
      },
    },
    monthCount: {
      get() {
        return moment(this.dateTo).diff(moment(this.dateFrom), 'months') + 1
      },
      set(newVal) {
        let dateTo = getEndDateByMonthCount(this.dateFrom, newVal)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
        this.$emit('update:dateTo', dateTo)
      },
    },
  },
  async created() {
    // 検索期間の初期値指定、任意の初期値がある場合はelseへ
    // 検索期限は外部APIから取得する必要がある為、ここに記述
    let dateFrom = this.dateFrom
    let dateTo = this.dateTo
    if (!dateFrom || !dateTo) {
      dateFrom = moment(this.maxDate)
        .subtract(DEFAULT_WEEK_COUNT, 'week')
        .add(1, 'days')
        .format(DATE_FORMAT)
      dateTo = this.maxDate
      // 検索期間が検索期限を超えていた場合の処理
      dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
      dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
      this.$emit('update:dateFrom', dateFrom)
      this.$emit('update:dateTo', dateTo)
    }
  },
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}

.term-input {
  display: flex;
}

.week-count {
  width: 70px;
}

.divider {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 16px;
  margin-right: 16px;
  white-space: nowrap;
}

// 期間選択エリアの切替アニメーション
.v-enter-active,
.v-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}
.v-enter {
  transform: translateX(-50%);
}
.v-leave-to {
  transform: translateX(50%);
}
</style>
